import {getTestProps} from '@benchling/js-test-utils';
import {Switch} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import {css, StyleSheet} from 'aphrodite';
import React, {useEffect, useState} from 'react';

import {trackEvent} from 'analytics/Analytics';
import {INSIGHTS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {convertRESTToWarehouseParameters} from 'insights/parameters/data/ParameterRESTConverters';
import {
  AnalyticsParameterConfigSpecRequest,
  RESTAnalyticsParameterValue,
} from 'insights/parameters/RESTParameterTypes';
import {runUntrustedSQLQuery} from 'insights/warehouse/AnalyticsWarehouseDAO';
import {RunQueryOutput} from 'insights/warehouse/AnalyticsWarehouseTypes';
import convertRunQueryOutputToLegacyOutput from 'insights/warehouse/convertRunQueryOutputToLegacyOutput';
import RunQueryOutputTable from 'insights/warehouse/RunQueryOutputTable';
import LLMFeedbackModal from 'llm/feedback/LLMFeedbackModal';
import {isLLMFeedbackEnabled} from 'llm/feedback/LLMFeedbackUtils';
import LLMLoadingMessage from 'llm/loading/LLMLoadingMessage';
import MonacoDiffEditor from 'llm/monaco/MonacoDiffEditor';
import MonacoEditor from 'llm/monaco/MonacoEditor';
import notifications from 'notifications';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import Modal from 'ui-library/modals/Modal';
import {isServerRequestError} from 'util/Net';

import tryFormatSQLQuery from '../block-editor/formatSQL';
import {InferredParameter, inferSQL, InferSQLResponse} from './SQLAssistantDAO';
import SQLAssistantFeedback, {FeedbackState} from './SQLAssistantFeedback';
import SQLAssistantSidePanel from './SQLAssistantSidePanel';

const BORDER_OFFSET = 2;
const MIN_EDITOR_HEIGHT = 218;
const MAX_EDITOR_HEIGHT = 600;
const PREVIEW_ROWS = 100;

export interface SQLAssistantModalProps {
  draftSql: string;
  parameterConfigs: Array<AnalyticsParameterConfigSpecRequest>;
  parameterValues: Array<RESTAnalyticsParameterValue>;
  onAccept: (sql: string, newParameterConfigs: Array<AnalyticsParameterConfigSpecRequest>) => void;
  onRequestClose: () => void;
}

const EXPLANATION = `The SQL Writer works by analyzing your query and attempting to help with any comments using the \`--!\` syntax.
The more information you provide the Writer the better it can help.

\`\`\`sql
--! Filter to trailing 30 days
SELECT * FROM registry_entity
\`\`\`

To further improve performance and accuracy consider giving important schemas and schema fields descriptions so the assistant has the proper context.`;

const LOADING_BLURBS = [
  'Submitting your query to the AI Writer…',
  'Analyzing your query…',
  'Extracting relevant tables…',
  'Loading warehouse schemas…',
  'Considering potential table joins…',
  'Picking most likely tables…',
  'Gathering sample data from likely tables…',
  'Coming up with a plan…',
  'Computing a SQL query suggestion…',
  'Validating SQL suggestion…',
  'Writing up a response…',
  'Almost there…',
  'Thanks for your patience, depending on your warehouse size and query complexity this can take a bit…',
];

const MEDIAN_LOADING_TIME_MS = 45000; // 45 seconds, adjust over time as latency improvements are made

function commentedError(err: string): string {
  return err
    .trim()
    .split('\n')
    .map((line) => `--! ${line}`)
    .join('\n');
}

interface UndoStackItem {
  inputPrompt: string;
  inferenceResult: InferSQLResponse | null;
}

export default function SQLAssistantModal(props: SQLAssistantModalProps): JSX.Element {
  const originalHasComments = props.draftSql.includes('--!');

  const [loadingInference, setLoadingInference] = useState(false);
  const [inferenceResult, setInferenceResult] = useState<InferSQLResponse | null>(null);
  const [suggestedSQL, setSuggestedSQL] = useState(props.draftSql);
  const [undoStack, setUndoStack] = useState<Array<UndoStackItem>>([]);

  const [inferrenceError, setInferrenceError] = useState<JSX.Element | string | null>(null);

  const [loadingDataset, setLoadingDataset] = useState(false);
  const [runQueryOutput, setRunQueryOutput] = useState<RunQueryOutput | null>(null);

  const [blurbIndex, setBlurbIndex] = useState(0);

  const [usefulFeedbackState, setUsefulFeedbackState] = useState<FeedbackState>(FeedbackState.NONE);

  const [diffMode, setDiffMode] = useState(false);
  const [diffEditorHeight, setDiffEditorHeight] = useState<number>(MIN_EDITOR_HEIGHT);

  const [feedbackModalOpened, {open: openFeedbackModal, close: closeFeedbackModal}] = useDisclosure(false);

  const runQuery = async (sql: string, parameters: Array<InferredParameter>): Promise<void> => {
    // run the query to get the resulting dataset
    setLoadingDataset(true);
    const runQueryResponse = await runUntrustedSQLQuery({
      parameterizedSQLQuery: `WITH dataset AS (${sql}\n) SELECT * FROM dataset LIMIT ${PREVIEW_ROWS}`,
      parameters: convertRESTToWarehouseParameters(props.parameterConfigs, props.parameterValues).concat(
        parameters
      ),
    });
    setLoadingDataset(false);
    setRunQueryOutput(runQueryResponse);
  };

  const runInference = async (): Promise<void> => {
    trackEvent(INSIGHTS_ANALYTICS_ACTIONS.REQUESTED_SQL_AI_ASSISTANT_SUGGESTION, {
      isRetry: inferenceResult != null,
    });
    if (inferrenceError) {
      setInferrenceError(null);
    }
    setBlurbIndex(0);
    setLoadingInference(true);
    const int = setInterval(
      () => {
        setBlurbIndex((prev) => Math.min(prev + 1, LOADING_BLURBS.length - 1));
      },
      Math.floor(MEDIAN_LOADING_TIME_MS / LOADING_BLURBS.length)
    );

    let inferenceResponse: InferSQLResponse;
    const inputPrompt = suggestedSQL || props.draftSql;

    try {
      inferenceResponse = await inferSQL(inputPrompt, props.parameterConfigs, props.parameterValues);
    } catch (err) {
      clearInterval(int);
      setLoadingInference(false);
      setInferrenceError(
        notifications.getFriendlyErrorAsElement(err, '') ??
          'Sorry, an unexpected error occurred. Please try again or reach out to support@benchling.com if the issue persists.'
      );
      if (suggestedSQL === '') {
        // to allow immediate retrying, populate the suggestion with the original
        setSuggestedSQL(props.draftSql);
      }
      if (isServerRequestError(err)) {
        return;
      }
      throw err;
    }

    clearInterval(int);
    // push onto prompt undo stack
    setUndoStack((prev) => [...prev, {inputPrompt, inferenceResult}]);
    setInferenceResult(inferenceResponse);
    setLoadingInference(false);
    setUsefulFeedbackState(FeedbackState.NONE);
    setSuggestedSQL(
      inferenceResponse.isValid
        ? inferenceResponse.suggestedSQL
        : `${inferenceResponse.suggestedSQL.trim()}\n\n${commentedError(inferenceResponse.sqlError)}`
    );

    if (inferenceResponse.isValid) {
      runQuery(inferenceResponse.suggestedSQL, inferenceResponse.parameters ?? []);
    }
  };

  const formatCurrentSQL = (): void => {
    setSuggestedSQL(tryFormatSQLQuery(suggestedSQL));
  };

  useEffect(() => {
    if (originalHasComments) {
      runInference();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Modal
      title={
        <div className={css(styles.modalTitleContainer)}>
          <h4 className={css(styles.modalTitle)}>SQL Writer</h4>
          <BenchlingIcon icon="sparkle" className={css(styles.purpleSparkle)} />
        </div>
      }
      onRequestClose={props.onRequestClose}
      onRequestSubmit={() => {
        trackEvent(INSIGHTS_ANALYTICS_ACTIONS.ACCEPTED_SQL_AI_ASSISTANT_SUGGESTION);
        props.onAccept(
          suggestedSQL,
          (inferenceResult?.parameters ?? []).map((p) => p.config)
        );
        props.onRequestClose();
      }}
      dataTestAttrs={{component: 'SQLAssistantModal', element: 'root'}}
      extraLarge={true}
      buttons={[
        {
          text: 'Accept SQL',
          submit: true,
          disabled: !suggestedSQL || loadingInference,
        },
      ]}
      closeableOnBackgroundClick={false}
      closeableOnEscapeKey={false}
      footer={
        <div className={css(styles.footerDisclaimer)}>
          <div>
            <p className={css(styles.disclaimerLine)}>This feature uses AI and may make mistakes.</p>
            <p className={css(styles.disclaimerLine)}>
              Always verify the results.{' '}
              <a
                href="https://help.benchling.com/hc/en-us/articles/30901318550157-Insights-SQL-assistant"
                target="_blank"
                rel="noreferrer"
              >
                Learn more.
              </a>
            </p>
          </div>
        </div>
      }
    >
      <div className={css(styles.paddedGrid)}>
        <div className={css(styles.containerGrid)}>
          <div className={css(styles.codePanel)}>
            <div className={css(styles.flexSpaceBetween)}>
              {/* Status messages */}
              <div className={css(styles.statusBlurb)}>
                {inferrenceError && (
                  <p
                    className={css(styles.errorMessage)}
                    {...getTestProps({
                      component: 'SQLAssistantModal',
                      element: 'errorMessage',
                    })}
                  >
                    {inferrenceError}
                  </p>
                )}

                {!inferrenceError && !loadingInference && inferenceResult == null && (
                  <div className={css(styles.flexRow)}>
                    <span>
                      Explain what help or changes you need in the SQL query using the <code>--!</code>{' '}
                      syntax. e.g. <code>--! Add a filter for date</code>
                    </span>
                  </div>
                )}

                {loadingInference && <LLMLoadingMessage text={LOADING_BLURBS[blurbIndex]} />}

                {!loadingInference && inferenceResult?.isValid && (
                  <>
                    <BenchlingIcon icon="check-circle" className={css(styles.doneIcon)} />
                    <span>
                      <strong>Done.</strong> Add more comments using the <code>--!</code> syntax to refine
                      results as needed.
                    </span>
                  </>
                )}

                {!loadingInference && inferenceResult != null && !inferenceResult.isValid && (
                  <div className={css(styles.flexRow)}>
                    <BenchlingIcon icon="exclamation-triangle" className={css(styles.warningIcon)} />
                    <span>
                      <strong>Generated Invalid SQL.</strong> We've inserted the error message into the code
                      so that you can simply retry with the "Generate" button.
                    </span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className={css(styles.actions)}>
                {undoStack.length > 0 && (
                  <Button
                    buttonStyle={ButtonStyle.TERTIARY_WHITE}
                    size={ButtonSize.SMALL}
                    onClick={() => {
                      const previousState = undoStack[undoStack.length - 1];
                      setInferenceResult(previousState.inferenceResult ?? null);
                      setSuggestedSQL(previousState.inputPrompt);
                      setUndoStack((prev) => prev.slice(0, -1));
                      if (previousState.inferenceResult?.isValid) {
                        runQuery(previousState.inputPrompt, previousState.inferenceResult?.parameters ?? []);
                      }
                    }}
                  >
                    Undo
                  </Button>
                )}
                <Button
                  buttonStyle={ButtonStyle.PRIMARY}
                  size={ButtonSize.SMALL}
                  disabled={!suggestedSQL || !suggestedSQL.includes('--!') || loadingInference}
                  onClick={runInference}
                >
                  Generate
                </Button>
              </div>
            </div>

            <div className={css(styles.controls)}>
              <Switch
                checked={diffMode}
                onChange={(e) => {
                  setDiffMode(e.currentTarget.checked);
                }}
                label="Compare to original"
                labelPosition="right"
              />
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.MINI}
                disabled={!suggestedSQL || loadingInference}
                onClick={formatCurrentSQL}
              >
                Format SQL
              </Button>
            </div>

            {diffMode ? (
              <>
                {inferenceResult != null && (
                  <div className={css(styles.split)}>
                    <strong>Original SQL</strong>
                    <div className={css(styles.flexRow)}>
                      <strong>Suggested SQL</strong>
                      <BenchlingIcon
                        icon="sparkle"
                        dataTestAttrs={{component: 'Icon', key: 'sparkle'}}
                        className={css(styles.graySparkle)}
                      />
                    </div>
                  </div>
                )}
                <MonacoDiffEditor
                  original={props.draftSql}
                  value={suggestedSQL}
                  language="pgsql"
                  width="100%"
                  height={diffEditorHeight}
                  options={{
                    lineDecorationsWidth: 8,
                    lineNumbersMinChars: 3,
                    originalEditable: false,
                    readOnly: loadingInference,
                    renderGutterMenu: false,
                    renderIndicators: false,
                    renderLineHighlightOnlyWhenFocus: true,
                    renderMarginRevertIcon: false,
                    renderOverviewRuler: false,
                    scrollbar: {
                      alwaysConsumeMouseWheel: false,
                    },
                    stickyScroll: {
                      enabled: false,
                    },
                    useInlineViewWhenSpaceIsLimited: false,
                  }}
                  className={css(styles.editor, loadingInference && styles.loadingEditor)}
                  onChange={(value) => {
                    setSuggestedSQL(value);
                  }}
                  onChangeContentSize={(e) => {
                    if (e.contentHeightChanged) {
                      setDiffEditorHeight(
                        Math.min(
                          Math.max(MIN_EDITOR_HEIGHT, e.contentHeight + BORDER_OFFSET),
                          MAX_EDITOR_HEIGHT
                        )
                      );
                    }
                  }}
                  theme="vs"
                />
              </>
            ) : (
              <>
                {inferenceResult != null && (
                  <div className={css(styles.flexRow)}>
                    <strong>Suggested SQL</strong>
                    <BenchlingIcon
                      icon="sparkle"
                      dataTestAttrs={{component: 'Icon', key: 'sparkle'}}
                      className={css(styles.graySparkle)}
                    />
                  </div>
                )}
                <MonacoEditor
                  value={suggestedSQL}
                  language="pgsql"
                  height={diffEditorHeight}
                  width="100%"
                  className={css(styles.editor, loadingInference && styles.loadingEditor)}
                  options={{
                    lineNumbersMinChars: 3,
                    minimap: {
                      enabled: false,
                    },
                    readOnly: loadingInference,
                    renderLineHighlightOnlyWhenFocus: true,
                    scrollbar: {
                      alwaysConsumeMouseWheel: false,
                    },
                    stickyScroll: {
                      enabled: false,
                    },
                  }}
                  onChange={(value) => {
                    setSuggestedSQL(value);
                  }}
                  onChangeContentSize={(e) => {
                    if (e.contentHeightChanged) {
                      setDiffEditorHeight(
                        Math.min(
                          Math.max(MIN_EDITOR_HEIGHT, e.contentHeight + BORDER_OFFSET),
                          MAX_EDITOR_HEIGHT
                        )
                      );
                    }
                  }}
                  theme="vs"
                />
              </>
            )}

            {isLLMFeedbackEnabled() && !loadingInference && inferenceResult != null && (
              <SQLAssistantFeedback
                usefulFeedbackState={usefulFeedbackState}
                setUsefulFeedbackState={setUsefulFeedbackState}
                openFeedbackModal={openFeedbackModal}
                traceId={inferenceResult.traceId}
              />
            )}
          </div>

          {/* Collapsible approach sidebar */}
          <SQLAssistantSidePanel markdown={inferenceResult?.approach ?? EXPLANATION} />
        </div>

        <div>
          {/* Table preview */}
          {inferenceResult?.isValid && (
            <>
              <h3 className={css(styles.previewHeader)}>Preview</h3>
              {loadingDataset && (
                <div className={css(styles.datasetLoadingContainer)}>
                  <CenteredHorizontalThrobber />
                  <span>Waiting for preview…</span>
                </div>
              )}
              {!loadingDataset && runQueryOutput != null && (
                <>
                  {runQueryOutput.dataFrame.rows.length === PREVIEW_ROWS && (
                    <p>Only showing first {PREVIEW_ROWS} results</p>
                  )}
                  <div className={css(styles.datasetContainer)}>
                    <RunQueryOutputTable
                      runQueryOutput={convertRunQueryOutputToLegacyOutput(runQueryOutput)}
                    />
                  </div>
                </>
              )}
            </>
          )}

          {/* Invalid query message */}
          {!inferenceResult?.isValid && inferenceResult?.sqlError != null && (
            <div className={css(styles.sqlErrorContainer)}>
              <h3 className={css(styles.previewHeader)}>Preview</h3>
              <pre className={css(styles.sqlError)}>{inferenceResult?.sqlError}</pre>
            </div>
          )}
        </div>
      </div>
      {inferenceResult && (
        <LLMFeedbackModal
          opened={feedbackModalOpened}
          onRequestClose={closeFeedbackModal}
          title="SQL Writer feedback"
          optionalContextItems={[
            {name: 'Original SQL', value: props.draftSql},
            {name: 'Suggested SQL', value: suggestedSQL},
          ]}
          requiredContextItems={[
            {
              name: 'Useful',
              value: usefulFeedbackState === FeedbackState.HELPFUL ? '👍' : '👎',
            },
            {
              name: 'Trace ID',
              value: inferenceResult.traceId,
            },
          ]}
        />
      )}
    </Modal>
  );
}

const styles = StyleSheet.create({
  containerGrid: {
    borderBottom: `1px solid ${Colors.GRAY_LIGHT}`,
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto',
  },
  flexRow: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  actions: {
    display: 'flex',
    gap: 8,
  },
  editor: {
    border: `1px solid ${Colors.GRAY_LIGHT}`,
  },
  flexSpaceBetween: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
    justifyContent: 'space-between',
  },
  loadingEditor: {
    backgroundColor: Colors.GRAY_LIGHT,
    opacity: 0.65,
  },
  flex: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  footerDisclaimer: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
    textAlign: 'left',
  },
  datasetContainer: {
    maxHeight: 380,
    overflowX: 'auto',
  },
  datasetLoadingContainer: {
    display: 'grid',
    justifyContent: 'center',
  },
  disclaimerLine: {
    color: Colors.GRAY_DARKER,
    marginBottom: 0,
  },
  statusBlurb: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
  },
  modalTitleContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  modalTitle: {
    margin: 0,
  },
  paddedGrid: {
    display: 'flex',
    flexDirection: 'column',
    fontSize: 13,
    gap: 8,
  },
  split: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
  },
  errorMessage: {
    backgroundColor: Colors.RED_LIGHTEST,
    border: `1px solid ${Colors.RED_DARK}`,
    borderRadius: 4,
    color: Colors.RED_DARK,
    padding: '4px 8px',
    textAlign: 'center',
  },
  doneIcon: {
    color: Colors.GREEN,
  },
  codePanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginRight: 8,
    paddingBottom: 8,
  },
  graySparkle: {
    color: Colors.GRAY_DARKER,
  },
  purpleSparkle: {
    color: Colors.PURPLE,
  },
  sqlErrorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  sqlError: {
    color: Colors.RED,
  },
  previewHeader: {
    fontSize: 14,
    fontWeight: 600,
    margin: '8px 0',
  },
  warningIcon: {
    color: Colors.ORANGE_LIGHT,
  },
  controls: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    justifyContent: 'space-between',
  },
});
